import { useSelector } from 'react-redux'
import { database, databaseRef, firestore } from './firebase'
import { searchClient } from './algolia'
import _ from 'lodash'
import axios from 'axios'
import {
  get,
  query,
  orderByKey,
  orderByChild,
  startAt,
  endAt,
  update,
  push,
  child
} from 'firebase/database'

import {
  collection, addDoc
} from 'firebase/firestore'
import store from './store'

export const isAdmin = () => {
  const { me: currentUser } = useSelector((state) => state.app)
  return currentUser && currentUser.role === 'Admin'
}
export const handleNavigation = (navigate, path) => {
  navigate(path)
}
export const toggle = (setOpen) => {
  setOpen((current) => !current)
}

// Search Functionality Start

// Utility to fetch users with indexing and caching
export const fetchUsers = async (database) => {
  try {
    // Add indexing for faster queries
    const usersRef = query(databaseRef(database, '/users'), orderByKey());
    const snapshot = await get(usersRef);
    return snapshot.exists() ? snapshot.val() : {};
  } catch (error) {
    console.error('Error fetching users:', error);
    return {};
  }
};

// Implement debounced search to prevent excessive database calls
const debouncedSearch = _.debounce((searchFn) => searchFn(), 300);

// Optimized search with indexing and filtering
export const searchUsersAndProfiles = async (searchString, memoizedFetchUsers) => {
  if (!searchString?.trim()) {
    return { hasSearched: false, response: null, loading: false };
  }

  const searchTerms = searchString.trim().toLowerCase().split(/\s+/);
  const isEmailSearch = searchString.includes('@');

  try {
    const allUsers = await memoizedFetchUsers();

    // Create an index map for faster lookups
    const userIndex = new Map();
    const results = [];

    // Process users and build index
    Object.entries(allUsers).forEach(([userId, user]) => {
      userIndex.set(userId, {
        emailTokens: user.email?.toLowerCase().split(/[.@]/),
        nameTokens: user.name?.toLowerCase().split(/\s+/),
        profiles: user.profiles
      });

      // Early match checking for main user
      const matchesSearch = isEmailSearch
        ? matchEmail(user.email, searchTerms)
        : matchName(user.name, searchTerms) || matchEmail(user.email, searchTerms);

      if (matchesSearch) {
        results.push({
          id: userId,
          name: user.name || 'Unknown',
          email: user.email || 'Unknown',
          owner: null,
        });
      }

      // Process profiles if they exist
      if (user.profiles) {
        const profileResults = processProfilesOptimized(
          userId,
          user,
          searchTerms,
          isEmailSearch
        );
        results.push(...profileResults);
      }
    });

    return {
      hasSearched: true,
      loading: false,
      response: results,
    };
  } catch (error) {
    console.error('Error searching users and profiles:', error);
    return { hasSearched: true, loading: false, response: null };
  }
};

// Optimized profile processing with better matching
const processProfilesOptimized = (userId, user, searchTerms, isEmailSearch) => {
  const profiles = [];

  Object.entries(user.profiles || {}).forEach(([profileKey, profile]) => {
    const profileNameTokens = profile.name?.toLowerCase().split(/\s+/) || [];

    const matchesCriteria = isEmailSearch
      ? matchEmail(user.email, searchTerms)
      : searchTerms.every(term =>
        profileNameTokens.some(token => token.includes(term))
      );

    if (matchesCriteria) {
      profiles.push({
        id: userId,
        owner: profileKey,
        name: `${profile.name || 'Profile'} (${user.name || 'Unknown'})`,
        parentEmail: user.email || 'Unknown',
      });
    }
  });

  return profiles;
};

// Efficient email matching
const matchEmail = (email, searchTerms) => {
  if (!email) return false;
  const emailLower = email.toLowerCase();
  return searchTerms.every(term => emailLower.includes(term));
};

// Efficient name matching
const matchName = (name, searchTerms) => {
  if (!name) return false;
  const nameTokens = name.toLowerCase().split(/\s+/);
  return searchTerms.every(term =>
    nameTokens.some(token => token.includes(term))
  );
};

export const createMemoizedSearchHandler = (database, setSearchedPatients) => {
  // Cache to store previous search results keyed by the trimmed query.
  const cache = new Map();

  const fetchFilteredUsers = async (searchString) => {
    const isEmailSearch = searchString.includes('@');
    const searchKey = isEmailSearch ? 'email' : 'name';

    try {
      const usersRef = query(
        databaseRef(database, '/users'),
        orderByChild(searchKey),
        startAt(searchString),
        endAt(searchString + '\uf8ff')
      );

      const snapshot = await get(usersRef);
      const users = snapshot.exists() ? snapshot.val() : {};

      const results = Object.entries(users).flatMap(([id, user]) => {
        const profiles = user.profiles
          ? Object.entries(user.profiles).map(([profileKey, profile]) => ({
            id,
            owner: profileKey,
            name: `${profile.name || 'Profile'} (${user.name || 'Unknown'})`,
            parentEmail: user.email || 'Unknown',
          }))
          : [];

        return [
          {
            id,
            name: user.name || 'Unknown',
            email: user.email || 'Unknown',
            owner: null,
          },
          ...profiles,
        ];
      });

      return results;
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  };

  return (searchString) => {
    const trimmed = searchString.trim();
    if (!trimmed) {
      setSearchedPatients({ hasSearched: false, response: null, loading: false });
      return;
    }

    // Return cached results if available.
    if (cache.has(trimmed)) {
      setSearchedPatients({
        hasSearched: true,
        response: cache.get(trimmed),
        loading: false,
      });
      return;
    }

    setSearchedPatients((prev) => ({ ...prev, loading: true }));

    fetchFilteredUsers(trimmed)
      .then((results) => {
        cache.set(trimmed, results);
        setSearchedPatients({ hasSearched: true, response: results, loading: false });
      })
      .catch((error) => {
        console.error('Search handler error:', error);
        setSearchedPatients({ hasSearched: true, response: null, loading: false });
      });
  };
};

// Search Functionality End

export const pickDifferentPairsWithDifferences = (obj1, obj2) => {
  const prev = {}
  const next = {}

  // Handle the case where obj2 is not provided
  if (!obj2) {
    Object.keys(obj1).forEach((key) => {
      prev[key] = null
      next[key] = obj1[key] ?? null // Use nullish coalescing to convert undefined to null
    })
    return { prev, new: next }
  }

  // Helper function to determine if two values are different, considering arrays
  const areDifferent = (value1, value2) => {
    if (_.isArray(value1) && _.isArray(value2)) {
      return (
        value1.length !== value2.length ||
        value1.some((item, index) => !_.isEqual(item, value2[index]))
      )
    }
    return !_.isEqual(value1, value2)
  }

  // Check differences from obj1 perspective
  Object.keys(obj1).forEach((key) => {
    if (!obj2.hasOwnProperty(key)) {
      prev[key] = null
      next[key] = obj1[key] ?? null // Convert undefined to null
    } else if (areDifferent(obj1[key], obj2[key])) {
      prev[key] = obj2[key] ?? null // Convert undefined to null
      next[key] = obj1[key] ?? null // Convert undefined to null
    }
  })

  // Check for keys that exist in obj2 but not in obj1
  Object.keys(obj2).forEach((key) => {
    if (!obj1.hasOwnProperty(key)) {
      prev[key] = obj2[key] ?? null // Convert undefined to null
      next[key] = null
    }
  })

  return { prev, new: next }
}

export const addLog = async (
  action,
  byID,
  byName,
  description,
  patientID,
  category,
  owner,
) => {
  // Validate patientID to prevent Firebase path errors
  if (!patientID) {
    console.error('Invalid patientID provided to addLog:', patientID);
    return; // Exit early if patientID is undefined, null, or empty
  }

  const newLogRef = push(databaseRef(database, `logs/patients/${patientID}/`));
  const key = newLogRef.key;
  const updates = {};
  updates[`/logs/patients/${patientID}/${key}`] = {
    action,
    byID,
    byName,
    description: description || '',
    timestamp: Date.now(),
    category: category || null,
    owner: owner || null,
  }
  await update(databaseRef(database), updates);
}

export const addDoctorLog = async (params) => {
  // if(params?.doctorID) {
  //   const newLogRef = push(child(databaseRef(database, `logs/doctors/${params?.doctorID}/`)));
  //   const key = newLogRef.key;
  //   const updates = {}
  //   updates[`/logs/doctors/${params?.doctorID}/${key}`] = {
  //     ...params, domain: "PHD", timestamp: Date.now()
  //   }
  //   await update(databaseRef(database), updates);
  // }
  // implement firestore here
  try {
    // Create a reference to the "events" subcollection within a document in the "logs" collection
    // The document ID could be fixed like "doctorLogs" or generated dynamically

    const ip = store.getState().app?.doctorIPAddress || "";

    const logsDocRef = collection(firestore, 'logs');
    const eventsCollectionRef = collection(logsDocRef, 'events', 'doctorActions');

    const newLogRef = await addDoc(eventsCollectionRef, {
      ...params,
      domain: 'PHD',
      timestamp: Date.now(),
      ipAddress: ip || null,
      details: {
        ...params.details,
        ip: ip || ""
      }
    });
    console.log("newLog", newLogRef);

    return newLogRef;
  }
  catch (e) {
    console.error('Error adding doctor log:', e);
  }
}

export const fetchAlgoliaData = async (indexName, query, startTS, endTS, options = {}) => {
  try {
    const index = searchClient.initIndex(indexName)
    let filters = ''

    if (startTS && endTS) {
      filters = `timestamp >= ${startTS} AND timestamp <= ${endTS}`
    }

    const searchOptions = {
      hitsPerPage: options.hitsPerPage || 20, // Default to 20 if not specified
      page: options.page || 0, // Default to first page
      ...options,
    }
    const { hits } = await index.search(query, searchOptions)

    return hits
  } catch (error) {
    console.error('Error fetching data from Algolia:', error)
    return []
  }
}
export const removeCamelCase = (str) => {
  let result = str.replace(/([A-Z])/g, ' $1').trim()
  return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase()
}
export const getInitials = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return 'N/A'

  const namesArray = fullName.trim().split(/\s+/)
  if (namesArray.length === 1) {
    return namesArray[0].charAt(0).toUpperCase()
  } else {
    return namesArray
      .filter((name, index) => index === 0 || index === namesArray.length - 1)
      .map((name) => name.charAt(0).toUpperCase())
      .join('')
  }
}
export const storeCloseChatIndex = async (conversationID) => {
  try {
    const indexStore = searchClient.initIndex(process.env.REACT_APP_ALGOLIA_CONVERSATIONS_NODE_INDEX_NAME)
    const archiveRef = databaseRef(database, `archiveConversations/${conversationID}`);
    const archiveSnap = await get(archiveRef);
    const archiveData = archiveSnap.exists() ? archiveSnap.val() : {};

    const convoRef = databaseRef(database, `/conversations/${conversationID}`);
    const convoSnap = await get(convoRef);
    let convos = '';
    const convoJSON = convoSnap.exists() ? convoSnap.val() : {};
    const convoValues = Object.values(convoJSON);
    convoValues.forEach(convoInstance => {
      if (convoInstance.type === 'text') {
        convos = `${convos} ${convoInstance.content}`;
      }
    });

    const doctorRef = databaseRef(database, `/doctors/${archiveData.doctorID}`);
    const doctorSnap = await get(doctorRef);
    const doctorData = doctorSnap.exists() ? doctorSnap.val() : {};

    const patientRef = databaseRef(database, `/users/${archiveData.patientID}`);
    const patientSnap = await get(patientRef);
    const patientData = patientSnap.exists() ? patientSnap.val() : {};

    let tags = "";
    if (archiveData.tagsList) {
      const tagslist = _.map(archiveData.tagsList, (val, uid) => {
        return { ...val, uid }
      })
      tagslist.forEach(tag => {
        if (tag.name) {
          tags = `${tags}, ${tag.name}`;
        }
      })
    };

    const chatCount = Object.keys(patientData.history_conversations || {});
    archiveData.tags = tags;
    archiveData.objectID = archiveSnap.key;
    archiveData.conversations = convos;
    archiveData.conversationID = archiveSnap.key;
    archiveData.patientEmail = patientData.email;
    archiveData.city = patientData.city;
    archiveData.country = patientData.country;
    archiveData.platform = patientData.platform;
    archiveData.DOB = patientData.DOB;
    archiveData.gender = patientData.gender;
    archiveData.symptoms = archiveData.symptoms || patientData.symptoms;
    archiveData.gmtOffset = patientData.gmtOffset;
    archiveData.buildNumber = patientData.buildNumber;
    archiveData.convoCount = chatCount.length;
    archiveData.doctorName = archiveData.doctorName || doctorData.doctorName;
    archiveData.doctorProfileImg = doctorData.profileImageURL;
    archiveData.doctorEmail = doctorData.email;
    archiveData.user_id = patientData.user_id;
    archiveData.healthInsuranceCardBack = patientData.healthInsuranceCardBack || null;
    archiveData.healthInsuranceCardFront = patientData.healthInsuranceCardFront || null;
    archiveData.healthInsuranceCardNumber = patientData.healthInsuranceCardNumber || null;
    console.log("")
    await indexStore.saveObject(
      archiveData,
      { autoGenerateObjectIDIfNotExist: true }
    );

  } catch (e) {
    console.error('Error storing data to Algolia:', e);
  }
}

export const sendCloseChatNotification = async (notificationToken, doctorName, chatId, doctorID) => {
  try {
    axios({
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      url: `https://us-central1-yourdoctorsonline-prod.cloudfunctions.net/closeChatInapp?notificationToken=${notificationToken}&doctorName=${doctorName}&chatId=${chatId}&doctorID=${doctorID}`,
      mode: 'cors',
    }).then(data => { }).catch(e => { })
    axios({
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      url: `https://us-central1-yourdoctorsonline-prod.cloudfunctions.net/closeChat?notificationToken=${notificationToken}&doctorName=${doctorName}&chatId=${chatId}&doctorID=${doctorID}`,
      mode: 'cors',
    }).then(data => { }).catch(e => { })

  } catch (e) {
    console.error('Error sending close chat notification:', e);
  }
}

const expireCheckoutSession = async (payload) => {
  const config = {
    method: 'post',
    url: `${process.env.REACT_APP_FIREBASE_CLOUD_FUNCTION_URL_NA}/stripe-expireCheckoutSession`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: payload,
  }
  const response = await axios(config)
  return response?.data || response
}

export const closeUserChat = async (chatID, me) => {
  try {
    const convoRef = databaseRef(database, `/openconversations/${chatID}`);
    const convoSnap = await get(convoRef);
    const currentConvo = convoSnap.exists() ? convoSnap.val() : {};
    const patientID = currentConvo?.patientID || null;
    const owner = currentConvo?.owner || null;

    const userRef = databaseRef(database, `/users/${patientID}`);
    const userSnap = await get(userRef);
    const userJSON = userSnap.exists() ? userSnap.val() : {};
    const { complete_address, homeAddress, sources = {}, notificationToken } = userJSON;
    const resolvedAddress = homeAddress || complete_address;

    if (convoSnap.exists()) {
      const archiveData = {
        ...currentConvo,
        start_timestamp: currentConvo?.timestamp || null,
        status: false,
        queued: false,
        timestamp: Date.now(),
        diagnosisValue: currentConvo?.assessment || null,
        assessment: currentConvo?.assessment || null,
        followUpData: null,
        medications: null,
        commentAdded: currentConvo?.plan || null,
        complete_address: resolvedAddress || "NA",
        owner
      }

      const closeData = {
        ...currentConvo,
        activeTimestamp: currentConvo.activeTimestamp || currentConvo.timestamp || null,
        start_timestamp: currentConvo.timestamp || null,
        status: false,
        queued: false,
        timestamp: Date.now(),
        location: chatID,
        diagnosisValue: currentConvo?.assessment || null,
        commentAdded: currentConvo?.plan || null
      }

      if (currentConvo?.prescriptionspdf) {
        closeData.billableChat = true;
      }

      const sourceKeys = Object.keys(sources);
      const sourceValues = Object.values(sources);
      let sourceTimestamp = null;
      let source = null;

      if (sourceKeys.length > 0) {
        sourceTimestamp = sourceValues[0];
        source = sourceKeys[0]
        sourceValues.forEach((sourceTime, index) => {
          if (sourceTime > sourceTimestamp) {
            sourceTimestamp = sourceTime;
            source = sourceKeys[index]
          }
        })
      }

      archiveData.source = source;
      closeData.source = source;

      const updates = {};
      updates[`/archiveConversations/${chatID}`] = archiveData;
      updates[`/users/${patientID}/conversations`] = null;
      updates[`/openconversations/${chatID}`] = null;
      updates[`/users/${patientID}/history_conversations/${chatID}`] = closeData;

      await update(databaseRef(database), updates);

      console.log("closing chat ", chatID);
      await storeCloseChatIndex(chatID);

      if (notificationToken && currentConvo?.doctorName && currentConvo?.doctorID && chatID) {
        await sendCloseChatNotification(notificationToken, currentConvo?.doctorName, chatID, currentConvo?.doctorID);
      }

      if (currentConvo.payAfterChat && !currentConvo.paymentCompleted && currentConvo.checkoutSessionID) {
        await expireCheckoutSession({
          sessionID: currentConvo.checkoutSessionID,
        }).catch(e => {
          console.error("closeUserChat stripe-expireCheckoutSession error ", e)
        })
      }

      if (currentConvo?.referencedChat?.chatID) {
        const parentChatID = currentConvo?.referencedChat?.chatID;
        const isLab = Boolean(currentConvo?.referencedChat?.isLab);

        try {
          const labTrackingRef = databaseRef(database, `/labTracking/${parentChatID}${isLab ? "_labs" : "_imaging"}`);
          const labTrackingSnap = await get(labTrackingRef);

          if (labTrackingSnap.exists()) {
            const updates = {};
            updates[`/labTracking/${parentChatID}${isLab ? "_labs" : "_imaging"}/oldSessions/${new Date().getTime()}`] = chatID;
            await update(databaseRef(database), updates);
          }
        } catch (e) {
          console.error("closeUserChat error ", e);
        }

        console.log(isLab, "isLab");
      }
      await addLog('CLOSE_CHAT', me?.id, me?.doctorName, { chatID }, patientID, 'conversation');
      await addDoctorLog({
        action: 'CLOSE_CHAT',
        category: 'conversation',
        doctorID: me?.id,
        description: `Chat closed ${me?.id !== currentConvo?.doctorID ? `of ${currentConvo?.doctorName}` : ""}`,
        details: {
          chatID,
          patientID: currentConvo?.patientID,
          patientName: userJSON?.name,
          doctorID: currentConvo?.doctorID,
          doctorName: currentConvo?.doctorName,
          owner: owner,
          timestamp: Date.now()
        }
      });
    } else {
      window.alert("Chat already closed!");
    }
  } catch (e) {
    console.error("closeUserChat error ", e);
    window.alert("Error closing chat, please try again later!");
  }
}
